// HM-335 cluster 16: FestiviSection - lista festivi anno corrente +
// add/delete. Recurring flag visibile.

import { Button, toast } from '@hotel-manager/ui/components';
import { Plus, Trash2 } from '@hotel-manager/ui/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { createHoliday, deleteHoliday, listHolidays } from '../../../api/pricing.js';

export function FestiviSection() {
  const queryClient = useQueryClient();
  const year = String(new Date().getFullYear());
  const holidaysQuery = useQuery({
    queryKey: ['holidays', year],
    queryFn: () => listHolidays(year),
  });

  const [adding, setAdding] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newRecurring, setNewRecurring] = useState(false);

  const createMutation = useMutation({
    mutationFn: () => createHoliday({ date: newDate, label: newLabel, recurring: newRecurring }),
    onSuccess: () => {
      toast.success('Festivo aggiunto');
      queryClient.invalidateQueries({ queryKey: ['holidays', year] });
      setAdding(false);
      setNewDate('');
      setNewLabel('');
      setNewRecurring(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteHoliday,
    onSuccess: () => {
      toast.success('Festivo rimosso');
      queryClient.invalidateQueries({ queryKey: ['holidays', year] });
    },
    onError: () => toast.error('Errore rimozione'),
  });

  if (holidaysQuery.isLoading) return <p className="text-text-secondary">Caricamento…</p>;
  const holidays = holidaysQuery.data?.holidays ?? [];

  return (
    <div className="space-y-4" data-testid="festivi-section">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-text-primary m-0">
          Festivi {year} ({holidays.length})
        </h2>
        {!adding && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setAdding(true)}
            data-testid="btn-add-holiday"
          >
            <Plus size={14} />
            Aggiungi festivo
          </Button>
        )}
      </div>

      {adding && (
        <div
          className="bg-bg-card border border-accent-primary rounded-xl p-4 flex items-center gap-3"
          data-testid="add-holiday-form"
        >
          <input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            className="border border-border-input rounded-md px-3 py-2 text-sm bg-bg-card outline-none focus:border-accent-primary"
            data-testid="input-date"
          />
          <input
            type="text"
            placeholder="Etichetta (es. Capodanno)"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            className="border border-border-input rounded-md px-3 py-2 text-sm bg-bg-card outline-none focus:border-accent-primary flex-1"
            data-testid="input-label"
          />
          <label className="flex items-center gap-1.5 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={newRecurring}
              onChange={(e) => setNewRecurring(e.target.checked)}
              data-testid="input-recurring"
            />
            Ricorrente
          </label>
          <Button
            variant="primary"
            size="sm"
            onClick={() => createMutation.mutate()}
            disabled={!newDate || !newLabel || createMutation.isPending}
            data-testid="btn-confirm-add"
          >
            Salva
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
            Annulla
          </Button>
        </div>
      )}

      <div className="bg-bg-card border border-border-base rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-base text-text-secondary text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Data</th>
              <th className="text-left px-4 py-3 font-semibold">Etichetta</th>
              <th className="text-left px-4 py-3 font-semibold">Ricorrente</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {holidays.map((h) => {
              const d = new Date(h.date);
              return (
                <tr
                  key={h.id}
                  className="border-t border-border-base"
                  data-testid={`holiday-${h.id}`}
                >
                  <td className="px-4 py-3 font-mono text-text-primary">
                    {d.toLocaleDateString('it-IT', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-4 py-3 text-text-primary">{h.label}</td>
                  <td className="px-4 py-3 text-xs text-text-secondary">
                    {h.recurring ? '✓' : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => deleteMutation.mutate(h.id)}
                      data-testid={`btn-delete-${h.id}`}
                      className="text-text-muted hover:text-state-guas p-1 rounded-md"
                      aria-label="Elimina"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
